import type { ReactNode } from "react"
import { 
  Layers, 
  Filter, 
  MousePointer2, 
  Database, 
  Library,
  Settings2, 
  Fingerprint
} from "lucide-react"
import { cn } from "../lib/utils"
import type { LayerExperience } from "../types/layer-experience"
import type { 
  MapWorkspaceSpriteRootSelectorConfig, 
  MapStyleAuthoringPaneProps,
  MapSourcesPaneProps 
} from "../contracts/map-workspace"
import { MapStyleAuthoringPane } from "./map-workspace/MapStyleAuthoringPane"
import { MapSourcesPane } from "./map-workspace/MapSourcesPane"
import { MapLayerSelector } from "./map-workspace/MapLayerSelector"
import { InteractivitySection } from "./sections/InteractivitySection"
import { FiltersSection, type FiltersSectionMode } from "./sections/FiltersSection"
import { DataSection } from "./sections/DataSection"
import { SourceInformationSection } from "./sections/SourceInformationSection"
import { AdvancedSection } from "./sections/AdvancedSection"

export type SectionId = "style" | "filters" | "interactions" | "data" | "source-info" | "advanced" | "sources"

type PaneSectionConfig = {
  id: SectionId
  label: string
  icon: typeof Layers
}

export interface LayerExperiencePaneProps {
  layer: LayerExperience
  activeSection?: SectionId
  onSectionChange?: (section: SectionId) => void
  onUpdateLayer?: (updater: (prev: LayerExperience) => LayerExperience) => void
  availableFields: Array<{ name: string; type: string }>
  stylePane?: MapStyleAuthoringPaneProps | null
  sourcesPane?: MapSourcesPaneProps | null
  spriteRootSelector?: MapWorkspaceSpriteRootSelectorConfig | null
  className?: string
  sections?: SectionId[]
  renderSection?: (section: SectionId) => ReactNode
  filterMode?: FiltersSectionMode
  footerContent?: ReactNode
}

export function LayerExperiencePane({
  layer,
  activeSection = "style",
  onSectionChange,
  onUpdateLayer,
  availableFields,
  stylePane,
  sourcesPane,
  spriteRootSelector,
  className,
  sections: visibleSections,
  renderSection,
  filterMode = "author",
  footerContent,
}: LayerExperiencePaneProps) {

  const handleUpdateLayer = onUpdateLayer ?? (() => undefined)

  const allSections: readonly PaneSectionConfig[] = [
    { id: "style", label: "Style", icon: Layers },
    { id: "filters", label: "Filters", icon: Filter },
    { id: "interactions", label: "Interactions", icon: MousePointer2 },
    { id: "data", label: "Data", icon: Database },
    { id: "source-info", label: "Source Info", icon: Fingerprint },
    { id: "advanced", label: "Advanced", icon: Settings2 },
    { id: "sources", label: "Sources", icon: Library },
  ] as const

  const targetScopedSections: SectionId[] = ["style", "filters", "interactions", "data", "source-info", "advanced"]
  const isTargetScoped = targetScopedSections.includes(activeSection)

  const sectionById = new Map(allSections.map(section => [section.id, section]))
  const sections = visibleSections
    ? visibleSections
        .map(sectionId => sectionById.get(sectionId))
        .filter((section): section is PaneSectionConfig => Boolean(section))
    : [...allSections]
  const customSectionContent = renderSection?.(activeSection)

  return (
    <div className={cn("flex min-w-0 w-full max-w-full flex-col h-full bg-background overflow-hidden", className)}>
      {/* Section Navigation */}
      <header className="flex items-center gap-2 overflow-x-auto border-b border-border bg-muted/30 px-4 py-1.5 shrink-0 scrollbar-thin scrollbar-thumb-muted">
        {sections.map((section) => {
          const Icon = section.icon
          const isActive = activeSection === section.id
          return (
            <button
              key={section.id}
              onClick={() => onSectionChange?.(section.id)}
              aria-label={section.label}
              className={cn(
                "group relative flex h-10 w-10 shrink-0 items-center justify-center rounded-control transition-all",
                isActive
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
              title={section.label}
            >
              <Icon className={cn("w-5 h-5", isActive ? "stroke-[2.5px]" : "stroke-[1.5px]")} />
              {isActive && (
                <div className="absolute -bottom-[7px] left-1/2 h-0.5 w-6 -translate-x-1/2 rounded-t-full bg-primary" />
              )}
            </button>
          )
        })}
      </header>

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Content Area */}
        <main className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-muted">
          {customSectionContent != null ? (
            <div className="h-full p-4">{customSectionContent}</div>
          ) : (
            <div className="p-4 flex flex-col gap-6">
              {/* Context and Scope Rows */}
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground/80 leading-none">
                    {sections.find(s => s.id === activeSection)?.label}
                  </h3>
                </div>

                {isTargetScoped && stylePane && (
                  <div className="animate-in fade-in slide-in-from-top-1 duration-200">
                    <MapLayerSelector 
                      value={stylePane.currentTargetId}
                      options={stylePane.targets.map(t => ({
                        id: t.id,
                        label: t.label,
                        metaLabel: t.bindingLabel ?? undefined,
                        description: t.summary ?? undefined,
                        disabled: !t.rendered
                      }))}
                      onValueChange={stylePane.onSelectTarget}
                    />
                  </div>
                )}
              </div>

              {/* Primary Editor Surface */}
              <div className="flex flex-col gap-6 min-h-0">
                {activeSection === "style" && (
                  stylePane ? (
                    <MapStyleAuthoringPane {...stylePane} />
                  ) : (
                    <div className="flex flex-col items-center justify-center p-8 text-center border border-dashed rounded-lg bg-muted/5">
                      <p className="text-xs font-medium text-muted-foreground italic">Style authoring is not available for this layer.</p>
                    </div>
                  )
                )}

                {activeSection === "sources" && (
                  sourcesPane ? (
                    <MapSourcesPane {...sourcesPane} embedded={true} />
                  ) : (
                    <div className="flex flex-col items-center justify-center p-8 text-center border border-dashed rounded-lg bg-muted/5">
                      <p className="text-xs font-medium text-muted-foreground italic">Source management is not available.</p>
                    </div>
                  )
                )}

                {activeSection === "filters" && (
                  <FiltersSection 
                    filters={layer.filters}
                    onUpdate={(patch) => handleUpdateLayer(prev => ({
                      ...prev,
                      filters: { ...prev.filters, ...patch }
                    }))}
                    availableFields={availableFields}
                    mode={filterMode}
                  />
                )}
                
                {activeSection === "interactions" && (
                  <InteractivitySection 
                    interactivity={layer.interactivity}
                  />
                )}

                {activeSection === "data" && (
                  <DataSection 
                    enrichments={layer.enrichments}
                    onUpdate={(patch) => handleUpdateLayer(prev => ({
                      ...prev,
                      enrichments: { ...prev.enrichments, ...patch }
                    }))}
                    availableFields={availableFields}
                    availableResolverCatalog={layer.enrichments?.resolver_definitions ?? []}
                  />
                )}

                {activeSection === "source-info" && (
                  <SourceInformationSection
                    enrichments={layer.enrichments}
                    onUpdate={(patch) => handleUpdateLayer(prev => ({
                      ...prev,
                      enrichments: { ...prev.enrichments, ...patch }
                    }))}
                    availableFields={availableFields}
                  />
                )}

                {activeSection === "advanced" && (
                  <AdvancedSection 
                    layer={layer}
                    onUpdate={(patch) => handleUpdateLayer(prev => ({
                      ...prev,
                      ...patch
                    }))}
                    spriteRootSelector={spriteRootSelector}
                  />
                )}
              </div>
            </div>
          )}
        </main>

        {/* Sticky Footer */}
        {footerContent && (
          <footer className="shrink-0 border-t border-border bg-muted/20 p-4">
            {footerContent}
          </footer>
        )}
      </div>
    </div>
  )
}
